import React, { useEffect, useRef } from "react";
import { View, StyleSheet, TouchableOpacity, Animated } from "react-native";
import CustomSearchInput from "../../../components/common/general/CustomSearchInput/CustomSearchInput";
import SortingDropdown from "../../../components/common/TableControls/SortingDropdown/SortingDropdown";
import CustomMultiSelect from "../../../components/common/general/CustomMultiSelect/CustomMultiSelect";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";

interface AdminTableHeaderWithCourseFilterProps {
  searchQuery?: string;
  setSearchQuery?: (query: string) => void;
  sortBy: string;
  setSortBy: (sortBy: string) => void;
  sortDropdownOpen: boolean;
  setSortDropdownOpen: (open: boolean) => void;
  sortItems: { label: string; value: string }[];
  placeholder?: string;
  coursesOnly: boolean;
  setCoursesOnly: (coursesOnly: boolean) => void;
  availableCourses: { key: string; value: string }[];
  selectedCourses: string[];
  setSelectedCourses: (courses: string[]) => void;
  showCourseFilter?: boolean;
  toggleLeftText?: string;
  toggleRightText?: string;
}

const AdminTableHeaderWithCourseFilter: React.FC<AdminTableHeaderWithCourseFilterProps> = ({
  searchQuery,
  setSearchQuery,
  sortBy,
  setSortBy,
  sortDropdownOpen,
  setSortDropdownOpen,
  sortItems,
  placeholder = "Search by any value",
  coursesOnly,
  setCoursesOnly,
  availableCourses,
  selectedCourses,
  setSelectedCourses,
  showCourseFilter = true,
  toggleLeftText = "All Sessions",
  toggleRightText = "Course Sessions Only",
}) => {
  const { isXXLarge } = useResponsiveScreen();
  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const shouldShow = coursesOnly;

    Animated.timing(fadeAnim, {
      toValue: shouldShow ? 1 : 0,
      duration: 150,
      useNativeDriver: true,
    }).start();
  }, [coursesOnly, fadeAnim]);

  return (
    <View style={styles.container}>
      <View style={styles.controlsRow}>
        <View style={styles.leftControls}>
          <View style={styles.sortContainer}>
            <SortingDropdown
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortDropdownOpen={sortDropdownOpen}
              setSortDropdownOpen={setSortDropdownOpen}
              sortItems={sortItems}
              label="Sort by"
            />
          </View>
          {showCourseFilter && (
            <View style={isXXLarge ? styles.utilitiesContainerXXLarge : styles.utilitiesContainer}>
              <>
                <CustomSpacing type="horizontal" size="m" />
                <View style={styles.toggleContainer}>
                  <TouchableOpacity
                    style={[
                      styles.toggleButton,
                      styles.toggleButtonLeft,
                      !coursesOnly && styles.toggleButtonActiveRight,
                    ]}
                    onPress={() => {
                      setCoursesOnly(false);
                      setSelectedCourses([]);
                    }}>
                    <CustomText
                      text={toggleLeftText}
                      size="s"
                      weight={!coursesOnly ? "bold" : "normal"}
                      style={[styles.toggleText, !coursesOnly && styles.toggleTextActive]}
                    />
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.toggleButton,
                      styles.toggleButtonRight,
                      coursesOnly && styles.toggleButtonActiveLeft,
                    ]}
                    onPress={() => setCoursesOnly(true)}>
                    <CustomText
                      text={toggleRightText}
                      size="s"
                      weight={coursesOnly ? "bold" : "normal"}
                      style={[styles.toggleText, coursesOnly && styles.toggleTextActive]}
                    />
                  </TouchableOpacity>
                </View>
              </>
              <Animated.View
                style={[
                  styles.animatedContainer,
                  {
                    opacity: fadeAnim,
                  },
                ]}
                pointerEvents={coursesOnly ? "auto" : "none"}>
                {isXXLarge && <CustomSpacing type="horizontal" size={"m"} />}
                <View style={styles.courseFilterContainer}>
                  <CustomMultiSelect
                    data={availableCourses}
                    selectedItems={selectedCourses}
                    setSelectedItems={setSelectedCourses}
                    label="Courses"
                    customButtonStyle={[styles.customMultiSelectButton, !isXXLarge && { marginTop: 12 }]}
                    textSize="s"
                    textWeight="normal"
                  />
                </View>
              </Animated.View>
            </View>
          )}
        </View>
        <View style={styles.rightControls}>
          {searchQuery !== undefined && setSearchQuery && (
            <View style={styles.searchContainer}>
              <CustomSearchInput placeholder={placeholder} searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 24,
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    zIndex: 5000,
    elevation: 5000,
  },
  controlsRow: {
    flexDirection: "row",
    gap: 16,
    alignItems: "flex-start",
    zIndex: 5001,
    justifyContent: "space-between",
  },
  leftControls: {
    flexDirection: "row",
    alignItems: "flex-start",
    flex: 1,
  },
  rightControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  sortContainer: {
    zIndex: 5002,
    elevation: 5002,
  },
  toggleContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    borderColor: "rgb(229, 231, 235)",
    zIndex: 3000,
    elevation: 3000,
    height: 42.5,
    backgroundColor: "white",
    borderRadius: 6,
    borderWidth: 1,
  },
  toggleButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: "#FFFFFF",
  },
  toggleButtonLeft: {
    borderRightWidth: 1,
    borderRightColor: "#D0D5DD",
  },
  toggleButtonRight: {},
  toggleButtonActiveLeft: {
    backgroundColor: "#0084FF",
    height: 42.5,
    justifyContent: "center",
    alignItems: "center",
    borderTopRightRadius: 6,
    borderBottomRightRadius: 6,
  },
  toggleButtonActiveRight: {
    backgroundColor: "#0084FF",
    height: 42.5,
    justifyContent: "center",
    alignItems: "center",
    borderTopLeftRadius: 6,
    borderBottomLeftRadius: 6,
  },
  toggleText: {
    color: "#6B7280",
  },
  toggleTextActive: {
    color: "#FFFFFF",
  },
  animatedContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  courseFilterContainer: {
    minWidth: 200,
    zIndex: 5003,
    elevation: 5003,
  },
  searchContainer: {
    flex: 1,
    maxWidth: 400,
    alignItems: "flex-end",
  },
  utilitiesContainer: {
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    marginLeft: 32,
  },
  utilitiesContainerXXLarge: {
    flexDirection: "row",
    alignItems: "center",
    marginLeft: 32,
  },
  customMultiSelectButton: {
    height: 42.5,
    borderColor: "rgb(229, 231, 235)",
    borderRadius: 6,
    borderWidth: 1,
    padding: 0,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
});

export default AdminTableHeaderWithCourseFilter;
